export type Opening = {
  id: string
  company: string
  title: string
  location: string
  pay: string
  tags: string[]
  department: string
}

export type JobStage = 'interested' | 'applied' | 'interview' | 'offer' | 'closed' | 'archived'
export type BoardJob = Opening & {
  stage: JobStage
  notes: string
  saved: string
  interviews: { name: string; date: string; location: string }[]
  offer: { salary: string; deadline: string } | null
}

export type Company = { name: string; about: string; checked: string; matches: string[] }
export type Conversation = {
  id: string
  company: string
  category: 'applications' | 'interviews' | 'outreach'
  subject: string
  role: string
  sender: string
  account: string
  body: string
  done: boolean
  replies: string[]
}

export const companies: Company[] = [
  {
    name: 'Ramp',
    about: 'Financial software',
    checked: '4h ago',
    matches: ['Backend · Remote', 'Any role · New York']
  },
  {
    name: 'Stripe',
    about: 'Payments infrastructure',
    checked: '6h ago',
    matches: ['Backend · Any location', 'Platform · New York']
  },
  { name: 'Mercury', about: 'Business banking', checked: '9h ago', matches: ['Backend · Remote'] },
  { name: 'Plaid', about: 'Financial infrastructure', checked: '3h ago', matches: ['Platform · Any location'] },
  { name: 'Linear', about: 'Developer tools', checked: '5h ago', matches: ['Any role · Remote'] },
  { name: 'Anthropic', about: 'AI research & tools', checked: '1d ago', matches: ['Platform · Any location'] }
]

export const openings: Opening[] = [
  {
    id: 'ramp-payments',
    company: 'Ramp',
    title: 'Senior Backend Engineer, Payments',
    location: 'Remote · United States',
    pay: '$190k–$240k',
    tags: ['Go', 'Postgres'],
    department: 'Engineering'
  },
  {
    id: 'stripe-infra',
    company: 'Stripe',
    title: 'Senior Engineer, Developer Infrastructure',
    location: 'Remote · United States',
    pay: '$200k–$260k',
    tags: ['Go', 'Distributed systems'],
    department: 'Engineering'
  },
  {
    id: 'mercury-backend',
    company: 'Mercury',
    title: 'Senior Backend Engineer, Core Banking',
    location: 'Remote · United States',
    pay: '$185k–$235k',
    tags: ['Haskell', 'Postgres'],
    department: 'Engineering'
  },
  {
    id: 'plaid-platform',
    company: 'Plaid',
    title: 'Senior Software Engineer, Platform',
    location: 'Remote · United States',
    pay: '$195k–$250k',
    tags: ['Go', 'Kafka'],
    department: 'Engineering'
  },
  {
    id: 'linear-product',
    company: 'Linear',
    title: 'Senior Product Engineer',
    location: 'Remote · United States',
    pay: '$180k–$230k',
    tags: ['TypeScript', 'React'],
    department: 'Engineering'
  },
  {
    id: 'anthropic-tools',
    company: 'Anthropic',
    title: 'Software Engineer, Developer Tools',
    location: 'San Francisco · Hybrid',
    pay: '$220k–$300k',
    tags: ['Python', 'Developer tools'],
    department: 'Engineering'
  }
]

export const followedOpenings: Opening[] = [
  openings[0],
  {
    id: 'stripe-billing',
    company: 'Stripe',
    title: 'Senior Backend Engineer, Billing',
    location: 'Remote · United States',
    pay: '$205k–$265k',
    tags: ['Ruby', 'APIs'],
    department: 'Engineering'
  },
  openings[4]
]

export const initialJobs: BoardJob[] = [
  {
    id: 'stripe-money',
    company: 'Stripe',
    title: 'Backend Engineer, Money Movement',
    location: 'Remote · US',
    pay: '$190k–$250k',
    tags: ['Ruby', 'APIs'],
    department: 'Engineering',
    stage: 'interested',
    saved: 'Yesterday',
    notes: '',
    interviews: [],
    offer: null
  },
  { ...openings[4], stage: 'interested', saved: 'Friday', notes: '', interviews: [], offer: null },
  {
    id: 'figma-platform',
    company: 'Figma',
    title: 'Software Engineer, Platform',
    location: 'San Francisco',
    pay: '$190k–$250k',
    tags: ['TypeScript'],
    department: 'Engineering',
    stage: 'applied',
    saved: 'Aug 25',
    notes: '',
    interviews: [],
    offer: null
  },
  {
    ...openings[3],
    stage: 'interview',
    saved: 'Aug 21',
    notes: 'Prepare questions about the platform team.',
    interviews: [{ name: 'Recruiter conversation', date: 'Tomorrow · 2:00 PM', location: 'Google Meet' }],
    offer: null
  },
  {
    id: 'mercury-offer',
    company: 'Mercury',
    title: 'Staff Engineer, Core Banking',
    location: 'Remote · US',
    pay: '$230k base',
    tags: ['Haskell', 'Postgres'],
    department: 'Engineering',
    stage: 'offer',
    saved: 'Aug 10',
    notes: 'Discuss the updated package with Jamie.',
    interviews: [],
    offer: { salary: '$230,000', deadline: 'Friday' }
  }
]

export const initialConversations: Conversation[] = [
  {
    id: 'mercury-offer',
    company: 'Mercury',
    category: 'applications',
    subject: 'An update on your offer',
    role: 'Staff Engineer, Core Banking',
    sender: 'Jamie Chen',
    account: 'alex@gmail.com',
    body: 'We have room to move on base. Would you have a few minutes to talk through the updated package?',
    done: false,
    replies: []
  },
  {
    id: 'plaid-interview',
    company: 'Plaid',
    category: 'interviews',
    subject: 'Looking forward to meeting',
    role: 'Senior Software Engineer, Platform',
    sender: 'Nina Patel',
    account: 'alex@gmail.com',
    body: 'We’re looking forward to our conversation tomorrow at 2:00 PM. We’ll talk about your experience and the platform team.',
    done: false,
    replies: []
  },
  {
    id: 'linear-intro',
    company: 'Linear',
    category: 'outreach',
    subject: 'An introduction from Linear',
    role: 'Senior Product Engineer',
    sender: 'Nina Patel',
    account: 'alex@hey.com',
    body: 'Your work on developer tools caught my eye. Would you be open to a short conversation about the team this week?',
    done: false,
    replies: []
  }
]

export const initialPortfolio = {
  name: 'Noor Sayegh',
  role: 'Visual artist · Chicago',
  headline: 'Objects.\nAfterimages.',
  introduction:
    'I work with paint, found surfaces, and the traces people leave behind. A small collection about memory taking physical form.',
  statement: 'I’m interested in the point where something ordinary starts to feel like a record of someone’s life.',
  linkLabel: 'Studio inquiries ↗',
  linkUrl: 'mailto:hello@example.com'
}

export const reviewDetails = {
  linear: {
    kicker: 'Time-sensitive · 1 of 3',
    title: 'An introduction from Linear',
    body: '',
    source: 'recruiting@example.com · Linear',
    copy: ''
  },
  ramp: {
    kicker: 'Time-sensitive · 2 of 3',
    title: 'Your Ramp offer needs a decision',
    body: 'The team is waiting for your response. Your notes and offer details are together on the board.',
    source: 'Ramp · Offer update',
    copy: '“We’re excited to invite you to join the team. Here are the details of your offer.”'
  },
  plaid: {
    kicker: 'Coming up · 3 of 3',
    title: 'Recruiter conversation tomorrow',
    body: 'A first conversation with the Plaid team. Review the role and the notes you’ve saved before you meet.',
    source: 'Plaid · Interview',
    copy: 'Tomorrow at 2:00 PM · 30 minutes'
  },
  complete: {
    kicker: 'Review complete',
    title: 'You’re all caught up.',
    body: 'Your conversations and opportunities are still here whenever you need them.',
    source: 'Your review queue',
    copy: 'All three items have been reviewed.'
  }
}
